import React, { useState } from 'react';
import { ArrowRight, Code2, Workflow, Layers } from 'lucide-react';

const views = [
  { title: 'Create',
    eyebrow: '01 / Build your coverage',
    description: 'Write an API test, use AI assistance, or build a browser flow. Keep the details in your control.',
    image: '/assets/portal-product-api.png',
    alt: 'API Testing for an example commerce workspace' },
  { title: 'Run',
    eyebrow: '02 / Put it to the test',
    description: 'Run the checks that matter to your release. Inspect each step and revisit the execution history.',
    image: '/assets/portal-product-flows.png',
    alt: 'Flow Testing with example checkout and account journeys' },
  { title: 'Understand',
    eyebrow: '03 / Make the next move',
    description: 'Review quality signals, find failing tests, and see which projects need attention.',
    image: '/assets/portal-product-overview.png',
    alt: 'Quality Overview for an example commerce workspace' },
];

export function LandingProductTour() {
  const [active, setActive] = useState(0);
  const view = views[active];
  return (
    <section id="product" className="scroll-mt-24 border-y border-slate-200/80 bg-white py-20 sm:py-24">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="mb-10 flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
          <div>
            <p className="mb-4 text-xs font-semibold uppercase tracking-[0.18em] text-violet-700">From first test to clear results</p>
            <h2 className="text-3xl font-semibold tracking-tight text-slate-950 sm:text-5xl">Less busywork.<br />A clearer testing workflow.</h2>
          </div>
          <p className="max-w-sm text-base leading-7 text-slate-500">One place to create your tests, run them, and understand what happened.</p>
        </div>
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-slate-50">
          <div role="tablist" aria-label="Product walkthrough" className="grid grid-cols-3 border-b border-slate-200 bg-white">
            {views.map((item, index) => <button key={item.title} id={`product-tab-${index}`} role="tab" aria-selected={active === index} aria-controls="product-tour-panel" tabIndex={active === index ? 0 : -1} onClick={() => setActive(index)} onKeyDown={(event) => {
              if (!['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(event.key)) return;
              event.preventDefault();
              const next = event.key === 'Home' ? 0 : event.key === 'End' ? 2 : (active + (event.key === 'ArrowRight' ? 1 : 2)) % 3;
              setActive(next);
              document.getElementById(`product-tab-${next}`)?.focus();
            }} className={`flex items-center justify-center gap-3 border-b-2 px-3 py-5 text-sm font-semibold transition-colors sm:text-base ${active === index ? 'border-violet-600 bg-violet-50/40 text-violet-700' : 'border-transparent text-slate-500 hover:bg-slate-50'}`}>
              <span className="hidden text-xs opacity-60 sm:inline">0{index + 1}</span>{item.title}</button>)}
          </div>
          <div role="tabpanel" id="product-tour-panel" aria-labelledby={`product-tab-${active}`} tabIndex={0} className="flex flex-col">
            <div className="flex flex-col justify-between gap-4 p-6 sm:flex-row sm:items-end lg:px-8">
              <div>
                <p className="mb-4 text-[10px] font-semibold uppercase tracking-widest text-violet-700">{view.eyebrow}</p>
                <p className="max-w-2xl text-base leading-7 text-slate-600">{view.description}</p>
              </div>
              <p className="shrink-0 text-xs text-slate-400">Example workspace · Real product screenshots</p>
            </div>
            <div className="min-w-0 overflow-hidden border-t border-slate-200 bg-white">
              <img key={view.image} src={view.image} alt={view.alt} width="2240" height="1600" loading="lazy" className="block h-auto w-full" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

const capabilities = [
  { icon: Code2,
    number: '01',
    title: 'Test your APIs.',
    description: 'Requests, assertions, environments and multi-step chains. Build checks around the services your product depends on.',
    route: '/solutions/api-testing',
    link: 'Explore API testing' },
  { icon: Workflow,
    number: '02',
    title: 'Cover the user journey.',
    description: 'Build browser flows for the paths that matter, from signing in to checking out. Review execution results step by step.',
    route: '/solutions/web-testing',
    link: 'Explore flow testing' },
  { icon: Layers,
    number: '03',
    title: 'Keep the team in sync.',
    description: 'Bring tests into projects and suites. Give teammates the access they need and a shared view of the work.',
    route: '/features',
    link: 'Explore the platform' },
];

export function LandingCapabilities({ navigateTo }) {
  return (
    <section className="py-20 sm:py-24">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <p className="mb-4 text-xs font-semibold uppercase tracking-[0.18em] text-violet-700">Built around your work</p>
        <h2 className="mb-12 max-w-2xl text-3xl font-semibold tracking-tight text-slate-950 sm:text-5xl">The right tools.<br />Working together.</h2>
        <div className="grid gap-10 md:grid-cols-3 md:gap-8">
          {capabilities.map(({ icon: Icon, ...item }) => <article key={item.number} className="border-t border-slate-300 pt-6">
            <div className="mb-8 flex items-center justify-between">
              <Icon size={25} strokeWidth={1.5} className="text-violet-700" />
              <span className="font-mono text-xs text-slate-400">/{item.number}</span>
            </div>
            <h3 className="mb-3 text-xl font-semibold text-slate-950">{item.title}</h3>
            <p className="mb-6 text-sm leading-7 text-slate-500">{item.description}</p>
            <button onClick={() => navigateTo(item.route)} className="inline-flex items-center gap-2 text-sm font-semibold text-slate-800 hover:text-violet-700">{item.link}<ArrowRight size={15} />
            </button>
          </article>)}
        </div>
      </div>
    </section>
  );
}
